import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner } from "@/components/common";
import { EmpresaUsuariaSelect, PLANTA_TEXTO, useEmpresasUsuarias, invalidarEmpresas } from "@/components/EmpresaUsuariaSelect";
import { Building2, Plus, Save, GitMerge, AlertTriangle } from "lucide-react";

const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-400";
const btn = "flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function FilaEmpresa({ e, todas, onCambio }) {
  const [nombre, setNombre] = useState(e.nombre);
  const [eqs, setEqs] = useState((e.equivalencias || []).join(", "));
  const [destino, setDestino] = useState("");
  const guardar = () => api.patch(`/empresas-usuarias/${e.id}`, { nombre, equivalencias: eqs.split(",").map((x) => x.trim()).filter(Boolean) })
    .then(() => { toast.success("Empresa actualizada"); onCambio(); }).catch(err);
  const activar = () => api.patch(`/empresas-usuarias/${e.id}`, { activa: !e.activa }).then(onCambio).catch(err);
  const unificar = () => {
    const d = todas.find((x) => x.id === destino);
    if (!d || !window.confirm(`¿Unificar ${e.nombre} dentro de ${d.nombre}? Sus empleados, contratos y expedientes pasan a ${d.nombre} y el nombre queda como equivalencia.`)) return;
    api.post(`/empresas-usuarias/${e.id}/unificar`, { destino_id: destino }).then(() => { toast.success("Empresas unificadas"); onCambio(); }).catch(err);
  };
  return (
    <div className={`space-y-2 px-4 py-3 ${e.activa ? "" : "bg-slate-50 opacity-80"}`} data-testid={`empresa-${e.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input value={nombre} onChange={(x) => setNombre(x.target.value)} className={`${inp} min-w-0 flex-1 font-medium`} data-testid={`empresa-nombre-${e.id}`} />
        <span className="text-xs text-slate-500"><strong className="text-emerald-700" data-testid={`empresa-activos-${e.id}`}>{e.activos} activos</strong> · {e.vinculaciones} vinculaciones</span>
        <button onClick={activar} className={btn} data-testid={`empresa-activa-${e.id}`}>{e.activa ? "Inactivar" : "Activar"}</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={eqs} onChange={(x) => setEqs(x.target.value)} placeholder="Nombres equivalentes (separados por coma)" className={`${inp} min-w-0 flex-1 text-xs`} data-testid={`empresa-eqs-${e.id}`} />
        <button onClick={guardar} className={btn} data-testid={`empresa-guardar-${e.id}`}><Save className="h-3.5 w-3.5" /> Guardar</button>
        <select value={destino} onChange={(x) => setDestino(x.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`empresa-destino-${e.id}`}>
          <option value="">Unificar dentro de…</option>
          {todas.filter((x) => x.id !== e.id).map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
        </select>
        <button onClick={unificar} disabled={!destino} className={btn} data-testid={`empresa-unificar-${e.id}`}><GitMerge className="h-3.5 w-3.5" /> Unificar</button>
      </div>
    </div>
  );
}

function NoIdentificada({ g, onCambio }) {
  const [sel, setSel] = useState(g.sugerencias[0]?.nombre || "");
  const [eq, setEq] = useState(true);
  const lista = useEmpresasUsuarias();
  const asignar = () => {
    const planta = sel === PLANTA_TEXTO;
    const e = lista.find((x) => x.nombre === sel);
    api.post("/empresas-usuarias/asignar", { clave: g.clave, empresa_id: e?.id, planta, guardar_equivalencia: eq && !planta })
      .then((r) => { toast.success(`Asignada (${r.data.vinculaciones} vinculaciones)`); onCambio(); }).catch(err);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm" data-testid={`noid-${g.clave}`}>
      <div className="min-w-[180px] flex-1"><p className="font-medium text-slate-800">“{g.texto}”</p><p className="text-xs text-slate-500">{g.vinculaciones} vinculaciones · {g.expedientes} expedientes · {g.contrataciones} contratos{g.sugerencias.length > 0 && ` · posible: ${g.sugerencias.map((s) => s.nombre).join(", ")}`}</p></div>
      <div className="w-64"><EmpresaUsuariaSelect value={sel} onChange={setSel} testid={`noid-select-${g.clave}`} /></div>
      <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={eq} onChange={(x) => setEq(x.target.checked)} /> Guardar como equivalencia</label>
      <button onClick={asignar} disabled={!sel} className={btn} data-testid={`noid-asignar-${g.clave}`}>Asignar</button>
    </div>
  );
}

function ResumenActivos({ todas, res }) {
  if (!todas || !res) return null;
  const top = todas.filter((e) => e.activos > 0).sort((a, b) => b.activos - a.activos);
  const max = Math.max(1, ...top.map((e) => e.activos));
  return (
    <div className="border-b border-slate-100 p-4" data-testid="empresas-resumen">
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-lg bg-slate-100 px-3 py-1.5">Trabajadores activos: <strong data-testid="resumen-activos">{res.activos}</strong></span>
        <span className="rounded-lg bg-vino-50 px-3 py-1.5 text-vino-800">De planta (Human): <strong data-testid="resumen-planta">{res.planta}</strong></span>
        <span className="rounded-lg bg-amber-50 px-3 py-1.5 text-amber-800">Empresa no identificada: <strong data-testid="resumen-noid">{res.no_identificada}</strong></span>
      </div>
      {top.length === 0 ? <p className="text-xs text-slate-400">Aún no hay trabajadores activos en misión.</p> : (
        <div className="space-y-1.5">{top.map((e) => (
          <div key={e.id} className="flex items-center gap-3 text-xs" data-testid={`resumen-empresa-${e.id}`}>
            <span className="w-40 shrink-0 truncate text-slate-700 sm:w-72" title={e.nombre}>{e.nombre}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-vino-600" style={{ width: `${(e.activos / max) * 100}%` }} /></span>
            <strong className="w-8 text-right text-slate-800">{e.activos}</strong>
          </div>
        ))}</div>
      )}
    </div>
  );
}

export function EmpresasUsuariasSection() {
  const [todas, setTodas] = useState(null);
  const [noid, setNoid] = useState([]);
  const [nueva, setNueva] = useState({ nombre: "", eqs: "" });
  const [ver, setVer] = useState(0);
  const [res, setRes] = useState(null);
  const load = () => {
    invalidarEmpresas();
    setVer((n) => n + 1);
    api.get("/empresas-usuarias/resumen").then((r) => setRes(r.data)).catch(() => setRes(null));
    api.get("/empresas-usuarias", { params: { todas: true } }).then((r) => setTodas(r.data)).catch(() => setTodas([]));
    api.get("/empresas-usuarias/no-identificadas").then((r) => setNoid(r.data)).catch(() => setNoid([]));
  };
  useEffect(load, []);
  const crear = () => api.post("/empresas-usuarias", { nombre: nueva.nombre, equivalencias: nueva.eqs.split(",").map((x) => x.trim()).filter(Boolean) })
    .then(() => { toast.success("Empresa registrada"); setNueva({ nombre: "", eqs: "" }); load(); }).catch(err);
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="empresas-usuarias-section">
      <div className="border-b border-slate-100 p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><Building2 className="h-5 w-5 text-vino-700" /> Empresas usuarias</h2>
        <p className="mt-1 text-sm text-slate-500">Catálogo único para Empleados, Contrataciones, Expedientes e Importaciones. HUMAN es el empleador: sus trabajadores se marcan como Trabajador de planta.</p>
      </div>
      <ResumenActivos todas={todas} res={res} />
      {noid.length > 0 && (
        <div className="border-b border-amber-100 bg-amber-50/40">
          <p className="flex items-center gap-2 px-4 pt-3 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Empresas no identificadas ({noid.length}) · elige la empresa correcta</p>
          <div className="divide-y divide-amber-100">{noid.map((g) => <NoIdentificada key={`${g.clave}-${ver}`} g={g} onCambio={load} />)}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 border-b border-slate-100 p-4">
        <input value={nueva.nombre} onChange={(e) => setNueva((n) => ({ ...n, nombre: e.target.value }))} placeholder="Razón social de la nueva empresa" className={`${inp} min-w-[200px] flex-1`} data-testid="empresa-nueva-nombre" />
        <input value={nueva.eqs} onChange={(e) => setNueva((n) => ({ ...n, eqs: e.target.value }))} placeholder="Equivalencias (opcional)" className={`${inp} min-w-[160px] flex-1`} data-testid="empresa-nueva-eqs" />
        <button onClick={crear} disabled={!nueva.nombre.trim()} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50" data-testid="empresa-nueva-btn"><Plus className="h-4 w-4" /> Registrar empresa</button>
      </div>
      {todas === null ? <div className="flex h-24 items-center justify-center text-vino-700"><Spinner /></div>
        : <div className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto">{todas.map((e) => <FilaEmpresa key={`${e.id}-${e.nombre}-${e.activa}-${(e.equivalencias || []).join("|")}`} e={e} todas={todas} onCambio={load} />)}</div>}
    </section>
  );
}
